import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Flag, Plus, RefreshCw, AlertCircle } from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const DEFAULT_FEATURE_FLAGS = [
  { id: 'demo_environment', name: 'Demo Environment', description: 'Enable "Try Demo" mode on login page for users to explore the platform in read-only mode', enabled: true, category: 'features' },
  { id: 'ai_question_generation', name: 'AI Question Generation', description: 'Allow League Admins to generate bonus questions using AI', enabled: true, category: 'features' },
  { id: 'google_oauth', name: 'Google OAuth Login', description: 'Allow users to sign in with Google', enabled: true, category: 'auth' },
  { id: 'email_password_auth', name: 'Email/Password Login', description: 'Allow users to sign in with email and password', enabled: true, category: 'auth' },
  { id: 'draft_mode', name: 'Draft Mode', description: 'Allow leagues to run player drafts', enabled: true, category: 'features' },
  { id: 'bulk_upload', name: 'Bulk Upload', description: 'Allow bulk upload of contestants via CSV', enabled: true, category: 'features' },
];

export default function SuperAdminFeatureFlags() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newFlag, setNewFlag] = useState({ id: '', name: '', description: '', enabled: false, category: 'features' });

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadFlags();
  }, [user, navigate]);

  const loadFlags = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform/feature-flags`, getAuthConfig());
      setFlags(response.data.length > 0 ? response.data : DEFAULT_FEATURE_FLAGS);
    } catch (error) {
      setFlags(DEFAULT_FEATURE_FLAGS);
    } finally {
      setLoading(false);
    }
  };

  const toggleFlag = async (flagId) => {
    const flag = flags.find(f => f.id === flagId);
    if (!flag) return;

    const updatedFlags = flags.map(f => 
      f.id === flagId ? { ...f, enabled: !f.enabled } : f
    );
    setFlags(updatedFlags);

    try {
      await axios.patch(`${API}/platform/feature-flags/${flagId}`, { enabled: !flag.enabled }, getAuthConfig());
      toast.success(`${flag.name} ${!flag.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      setFlags(flags);
      toast.error('Failed to update feature flag');
    }
  };

  const addFlag = async () => {
    if (!newFlag.id || !newFlag.name) {
      toast.error('ID and Name are required');
      return;
    }
    try {
      const response = await axios.post(`${API}/platform/feature-flags`, newFlag, getAuthConfig());
      setFlags([...flags, response.data || newFlag]);
      setShowAddDialog(false);
      setNewFlag({ id: '', name: '', description: '', enabled: false, category: 'features' });
      toast.success('Feature flag added');
    } catch (error) {
      setFlags([...flags, { ...newFlag }]);
      setShowAddDialog(false);
      toast.success('Feature flag added (local)');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      features: 'bg-blue-600',
      auth: 'bg-purple-600',
      notifications: 'bg-amber-600',
      visibility: 'bg-emerald-600',
      access: 'bg-red-600'
    };
    return colors[category] || 'bg-zinc-700';
  };

  if (!user?.is_super_admin) return null;

  const groupedFlags = flags.reduce((acc, flag) => {
    const category = flag.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(flag);
    return acc;
  }, {});

  return (
    <SuperAdminLayout backLink="/super-admin">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header - Title + Actions on same line */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">Feature Flags</h1>
            <p className="text-xs lg:text-sm text-zinc-400">Control features</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={loadFlags} className="text-zinc-300">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">Add</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedFlags).map(([category, categoryFlags]) => (
              <Card key={category} className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-2 sm:pb-3">
                  <CardTitle className="text-white capitalize flex items-center gap-2 text-base sm:text-lg">
                    <Flag className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
                    {category.replace('_', ' ')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {categoryFlags.map(flag => (
                      <div key={flag.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                        <div className="flex-1 min-w-0 mr-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-white text-sm">{flag.name}</span>
                            <Badge className={`${getCategoryColor(flag.category)} text-xs`}>
                              {flag.enabled ? 'ON' : 'OFF'}
                            </Badge>
                          </div>
                          <p className="text-xs text-zinc-400 mt-1 line-clamp-1">{flag.description}</p>
                        </div>
                        <Switch
                          checked={flag.enabled}
                          onCheckedChange={() => toggleFlag(flag.id)}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-zinc-400">
                Feature flags control platform functionality globally. Changes take effect immediately.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Flag Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Feature Flag
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <Label className="text-zinc-300 text-sm">Flag ID</Label>
              <Input
                value={newFlag.id}
                onChange={(e) => setNewFlag({...newFlag, id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                placeholder="my_feature_flag"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Name</Label>
              <Input
                value={newFlag.name}
                onChange={(e) => setNewFlag({...newFlag, name: e.target.value})}
                placeholder="My Feature"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Description</Label>
              <Textarea
                value={newFlag.description}
                onChange={(e) => setNewFlag({...newFlag, description: e.target.value})}
                placeholder="What this flag controls..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="text-zinc-300 w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={addFlag} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
              Add Flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
